import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import type * as signalR from "@microsoft/signalr";
import { useGetIdentity } from "@refinedev/core";
import { APP_CONFIG } from "../constants";
import { chLogger } from "../components/DebugOverlay";
import { useToast } from "./ToastContext";
import { isTransientNetworkError } from "../utils/clientEnvironment";

interface SignalRContextType {
    connection: signalR.HubConnection | null;
    isConnected: boolean;
    latestNotification: { id: string; message: string } | null;
    unreadMessageCount: number;
    fetchUnreadMessageCount: () => void;
}

const SignalRContext = createContext<SignalRContextType | undefined>(undefined);

let sharedConnection: signalR.HubConnection | null = null;
let signalRModulePromise: Promise<typeof import("@microsoft/signalr")> | null = null;
let sharedStartPromise: Promise<void> | null = null;
let sharedHeartbeatTimer: ReturnType<typeof setInterval> | null = null;

let activeOnReconnecting: (() => void) | null = null;
let activeOnReconnected: (() => void) | null = null;
let activeOnClose: (() => void) | null = null;

const ensureConnection = async (token: string) => {
    if (!sharedConnection) {
        signalRModulePromise ??= import("@microsoft/signalr");
        const signalRModule = await signalRModulePromise;
        sharedConnection = new signalRModule.HubConnectionBuilder()
            .withUrl(`${APP_CONFIG.API_URL}/notificationHub`, {
                accessTokenFactory: () => localStorage.getItem(APP_CONFIG.STORAGE_KEYS.TOKEN) || token,
            })
            .withAutomaticReconnect()
            .configureLogging(signalRModule.LogLevel.None)
            .build();

        sharedConnection.onreconnecting(() => activeOnReconnecting?.());
        sharedConnection.onreconnected(() => activeOnReconnected?.());
        sharedConnection.onclose(() => activeOnClose?.());
    }
    return sharedConnection;
};

const startConnectionSingleton = async (token: string) => {
    const connection = await ensureConnection(token);

    if (connection.state === "Connected") {
        return connection;
    }

    if (!sharedStartPromise) {
        sharedStartPromise = connection.start()
            .then(async () => {
                const isForeground = typeof document !== "undefined" ? document.visibilityState === "visible" : true;
                await connection.invoke("Heartbeat", isForeground).catch(() => {});
                if (!sharedHeartbeatTimer) {
                    sharedHeartbeatTimer = setInterval(() => {
                        if (connection.state === "Connected") {
                            const fg = typeof document !== "undefined" ? document.visibilityState === "visible" : true;
                            connection.invoke("Heartbeat", fg).catch(() => {});
                        }
                    }, 60000);
                }
            })
            .catch((err) => {
                if (connection.state === "Disconnected") {
                    sharedConnection = null;
                }
                throw err;
            })
            .finally(() => {
                sharedStartPromise = null;
            });
    }

    await sharedStartPromise;
    return connection;
};

const stopConnectionSingleton = async () => {
    if (sharedHeartbeatTimer) {
        clearInterval(sharedHeartbeatTimer);
        sharedHeartbeatTimer = null;
    }

    if (sharedConnection) {
        try {
            await sharedConnection.stop();
        } catch {
            // no-op
        }
    }

    sharedConnection = null;
    sharedStartPromise = null;
};

export const SignalRProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { data: identity } = useGetIdentity<{ id: number }>();
    const [isConnected, setIsConnected] = useState(false);
    const [latestNotification, setLatestNotification] = useState<{ id: string; message: string } | null>(null);
    const [unreadMessageCount, setUnreadMessageCount] = useState(0);
    const connectionRef = useRef<signalR.HubConnection | null>(sharedConnection);
    const toast = useToast();

    const fetchUnreadMessageCount = useCallback(async () => {
        const token = localStorage.getItem(APP_CONFIG.STORAGE_KEYS.TOKEN);
        if (!token) return;
        if (typeof navigator !== "undefined" && navigator.onLine === false) return;
        if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
        try {
            const resp = await fetch(`${APP_CONFIG.API_URL}/api/Chat/unread-count`, {
                headers: { "Authorization": `Bearer ${token}` }
            });
            if (resp.ok) {
                const data = await resp.json();
                setUnreadMessageCount(data.count || 0);
            }
        } catch (err) {
            if (!isTransientNetworkError(err)) {
                console.error("Failed to fetch unread message count", err);
            }
        }
    }, []);

    useEffect(() => {
        if (!identity?.id) {
            void stopConnectionSingleton();
            connectionRef.current = null;
            setIsConnected(false);
            setUnreadMessageCount(0);
            return;
        }

        const token = localStorage.getItem(APP_CONFIG.STORAGE_KEYS.TOKEN);
        if (!token) return;

        let disposed = false;
        let activeConnection: signalR.HubConnection | null = null;

        const onReceiveNotification = (message: string) => {
            console.log(`[NOTIFICATION] Live Received: ${message}`);
            chLogger(`[NOTIFICATION] Live Received: ${message}`);
            setLatestNotification({ id: Date.now().toString(), message });
            if (!message.includes("Gizemli Sans Sandigi")) {
                toast.info(message);
            }
        };

        const onReceiveMessage = () => {
            void fetchUnreadMessageCount();
        };

        const onReceiveMessagesRead = () => {
            void fetchUnreadMessageCount();
        };

        const onReconnecting = () => setIsConnected(false);
        const onReconnected = () => {
            setIsConnected(true);
            const isForeground = typeof document !== "undefined" ? document.visibilityState === "visible" : true;
            activeConnection?.invoke("Heartbeat", isForeground).catch(() => {});
        };
        const onClose = () => setIsConnected(false);

        const init = async () => {
            try {
                const connection = await startConnectionSingleton(token);
                if (disposed) return;

                activeConnection = connection;
                connectionRef.current = connection;

                connection.off("ReceiveNotification", onReceiveNotification);
                connection.on("ReceiveNotification", onReceiveNotification);

                connection.off("ReceiveMessage", onReceiveMessage);
                connection.on("ReceiveMessage", onReceiveMessage);

                connection.off("ReceiveMessagesRead", onReceiveMessagesRead);
                connection.on("ReceiveMessagesRead", onReceiveMessagesRead);

                activeOnReconnecting = onReconnecting;
                activeOnReconnected = onReconnected;
                activeOnClose = onClose;

                setIsConnected(connection.state === "Connected");
                void fetchUnreadMessageCount();

                const onVisibility = () => {
                    if (document.visibilityState === "visible" && connection.state === "Disconnected") {
                        void startConnectionSingleton(token).catch(() => {});
                    }
                    if (document.visibilityState === "visible") {
                        void fetchUnreadMessageCount();
                    }
                };
                document.addEventListener("visibilitychange", onVisibility);
                (connection as any).__chOnVisibility = onVisibility;
            } catch {
                setIsConnected(false);
            }
        };

        const runInit = () => {
            if (typeof window !== "undefined" && (window as any).requestIdleCallback) {
                (window as any).requestIdleCallback(() => {
                    void init();
                }, { timeout: 3000 });
            } else {
                setTimeout(() => {
                    void init();
                }, 1500);
            }
        };

        runInit();

        return () => {
            disposed = true;
            activeOnReconnecting = null;
            activeOnReconnected = null;
            activeOnClose = null;
            if (activeConnection) {
                activeConnection.off("ReceiveNotification", onReceiveNotification);
                activeConnection.off("ReceiveMessage", onReceiveMessage);
                activeConnection.off("ReceiveMessagesRead", onReceiveMessagesRead);
                const onVisibility = (activeConnection as any).__chOnVisibility as (() => void) | undefined;
                if (onVisibility) {
                    document.removeEventListener("visibilitychange", onVisibility);
                    delete (activeConnection as any).__chOnVisibility;
                }
            }
        };
    }, [identity?.id, toast, fetchUnreadMessageCount]);

    return (
        <SignalRContext.Provider value={{ connection: connectionRef.current, isConnected, latestNotification, unreadMessageCount, fetchUnreadMessageCount }}>
            {children}
        </SignalRContext.Provider>
    );
};

export const useSignalRContext = () => {
    const context = useContext(SignalRContext);
    if (context === undefined) {
        throw new Error("useSignalRContext must be used within a SignalRProvider");
    }
    return context;
};
